import { useEffect, useRef } from "react"
import { Platform, Pressable, View, TextInput as TI } from "react-native"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"

import { ar } from "@src/i18n/ar"
import { trpc } from "@src/utils/trpc"

import { ModalWrapper, Text, TextInput, MCIcon } from "./core"
import { adjustStockInput } from "@white-labels/input"

interface AdjustStockModalProps {
  onClose: () => void
  productId: number
  storagePlaceId: number
  currentQuantity: number
  isWeightProduct: boolean
}

export function AdjustStockModal({ onClose, productId, storagePlaceId, currentQuantity, isWeightProduct }: AdjustStockModalProps) {
  const utils = trpc.useUtils()
  const adjustStock = trpc.inventory.adjustStock.useMutation()
  const quantityRef = useRef<TI | null>(null)

  const displayCurrentQty = isWeightProduct ? (currentQuantity / 1000).toFixed(3) : currentQuantity.toString()

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(adjustStockInput),
    defaultValues: { productId, storagePlaceId },
    mode: "all",
  })

  const loading = adjustStock.isPending
  const { isValid } = formState
  const disabled = !isValid || loading

  const onSubmit = handleSubmit((data) => {
    const newQuantity = isWeightProduct ? Math.round(data.newQuantity * 1000) : data.newQuantity

    adjustStock.mutate(
      { ...data, newQuantity },
      {
        onSuccess: () => {
          utils.products.get.invalidate({ id: productId })
          utils.storagePlaces.listProducts.invalidate()
          onClose()
        },
        onError: (error) => {
          alert(error.message || ar.somethingWentWrong)
        },
      },
    )
  })

  // Auto-focus on open
  useEffect(() => {
    requestAnimationFrame(() => quantityRef.current?.focus())
  }, [])

  // Close on Escape (web)
  useEffect(() => {
    if (Platform.OS === "web") {
      const handler = (e: KeyboardEvent) => {
        if (e.key === "Escape") onClose()
      }
      window.addEventListener("keydown", handler)
      return () => window.removeEventListener("keydown", handler)
    }
  }, [onClose])

  return (
    <ModalWrapper onClose={onClose} className="rounded-none shadow-none p-4 max-w-lg">
      <View className="flex-row items-center justify-between mb-4">
        <Text className="text-lg font-bold text-gray-900 tracking-tight">{ar.adjustStock}</Text>
        <Pressable onPress={onClose} className="p-2">
          <MCIcon name="close" size={22} color="#6B7280" />
        </Pressable>
      </View>

      {/* Current Quantity Display */}
      <View className="bg-gray-50 p-3 mb-4 border border-gray-200">
        <Text className="text-sm text-gray-600 font-bold">{ar.qty}</Text>
        <Text className="text-xl font-bold text-gray-900">
          {displayCurrentQty} {isWeightProduct ? ar.kg : ""}
        </Text>
      </View>

      {/* New Quantity Input */}
      <Text className="text-base font-bold text-gray-900 mb-2">{ar.newQuantity}</Text>
      <View className="relative mb-4">
        <Controller
          control={control}
          name="newQuantity"
          render={({ field: { onChange, value } }) => (
            <TextInput
              ref={quantityRef}
              placeholder="0"
              placeholderTextColor="#9CA3AF"
              keyboardType="decimal-pad"
              textAlign="right"
              selectTextOnFocus
              value={value?.toString() ?? ""}
              onChangeText={(text) => {
                const num = Number(text)
                if (Number.isNaN(num)) return
                onChange(num)
              }}
            />
          )}
        />

        {isWeightProduct && (
          <View className="absolute left-4 top-1/2 -translate-y-1/2">
            <Text className="text-gray-500 font-medium text-sm">{ar.kg}</Text>
          </View>
        )}
      </View>

      {/* Reason Input */}
      <Text className="text-base font-bold text-gray-900 mb-2">{ar.adjustStockReason}</Text>
      <Controller
        control={control}
        name="reason"
        render={({ field: { onChange, value } }) => (
          <TextInput placeholder={ar.addOptionalNote} placeholderTextColor="#9CA3AF" multiline textAlignVertical="top" value={value} onChangeText={onChange} />
        )}
      />

      {/* Submit Button */}
      <Pressable
        className={`h-8 mt-2 items-center justify-center active:opacity-90 disabled:opacity-50 ${disabled ? "bg-gray-400" : "bg-blue-600"}`}
        onPress={onSubmit}
        disabled={disabled}
      >
        <Text className="text-white font-bold text-lg tracking-wide">{loading ? ar.processing : ar.confirm}</Text>
      </Pressable>
    </ModalWrapper>
  )
}
